import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "unatesis.currency";
const DEFAULT_CURRENCY = "USD";
const SUPPORTED = ["USD", "MXN"];

const CurrencyContext = createContext({
  currency: DEFAULT_CURRENCY,
  setCurrency: () => {},
  rate: 1,
  fetchedAt: null,
  format: (usd) => `$${usd} USD`,
});

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_CURRENCY;
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_CURRENCY;
  });
  const [rate, setRate] = useState(1);
  const [fetchedAt, setFetchedAt] = useState(null);

  useEffect(() => {
    if (currency === "USD") {
      setRate(1);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/billing/fx-rate?target=${currency}`);
        if (data?.rate) {
          setRate(data.rate);
          setFetchedAt(data.fetched_at);
        }
      } catch {
        // keep previous rate
      }
    })();
  }, [currency]);

  const setCurrency = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setCurrencyState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const format = useCallback(
    (usdAmount) => {
      const amount = Number(usdAmount) || 0;
      if (currency === "USD") {
        return `$${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })} USD`;
      }
      const converted = amount * rate;
      // Round to the nearest 10 MXN for a clean display ($1,189 → $1,190)
      const rounded = Math.round(converted / 10) * 10;
      return `$${rounded.toLocaleString("es-MX", { maximumFractionDigits: 0 })} MXN`;
    },
    [currency, rate]
  );

  const value = useMemo(
    () => ({ currency, setCurrency, rate, fetchedAt, format }),
    [currency, setCurrency, rate, fetchedAt, format]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

/** UI toggle. Compact pill, switches USD ↔ MXN. */
export function CurrencyToggle({ className = "", testid = "currency-toggle" }) {
  const { currency, setCurrency } = useCurrency();
  return (
    <div
      data-testid={testid}
      role="group"
      aria-label="Cambiar moneda"
      className={`inline-flex items-center rounded-full bg-paper-2 border border-rule p-0.5 text-[11px] ${className}`}
    >
      {SUPPORTED.map((c) => {
        const active = currency === c;
        return (
          <button
            key={c}
            data-testid={`${testid}-${c.toLowerCase()}`}
            data-active={active ? "true" : "false"}
            onClick={() => setCurrency(c)}
            className={`px-3 py-1 rounded-full font-medium tracking-wide transition-colors ${
              active ? "bg-forest text-paper" : "text-ink-soft hover:text-ink"
            }`}
          >
            {c}
          </button>
        );
      })}
    </div>
  );
}
